import * as React from "react";

/**
 * Embed mode: what a host's own website may ask for when it frames a rolling link in an iframe.
 *
 * <p>The page was built as a destination. It wears a full-height background, generous page
 * padding and a banner header, all of which are right when the page IS the tab and wrong when it
 * is a 700px box in the middle of somebody else's marketing site. Embed mode is the single switch
 * that turns a destination into a component.</p>
 *
 * <p><b>Rolling links only.</b> A rolling link is long-lived and deliberately memorable, which is
 * what makes it the one worth putting on a website; ad-hoc pages are frozen one-off snapshots and
 * polls are shared by link. Only {@code PublicRollingLinkPage} reads any of this, so the ad-hoc
 * and poll pages ignore these parameters entirely rather than honouring flags for a feature that
 * does not reach them. CloudFront agrees: only {@code /@owner/link} is frameable at all.</p>
 *
 * <h2>Read once, from the URL</h2>
 *
 * <p>The public pages are matched by pathname in {@code App.tsx} BEFORE the router, and nothing
 * client-side ever navigates between them, so the query string cannot change under a mounted
 * page. That is why {@link useEmbedOptions} reads {@code window.location.search} once and never
 * subscribes to anything.</p>
 *
 * <h2>Suppression requires {@code embed=1}, and that is not a security control</h2>
 *
 * <p>{@code hide_header} and {@code hide_details} do nothing unless {@code embed=1} is also set.
 * That keeps the parameters coherent: they describe how the page renders as a component, so they
 * only mean anything once it is one. It is <b>not</b> protection, and an earlier version of this
 * comment wrongly said it was. Anybody can append {@code ?embed=1&hide_header=1} to a public link
 * just as easily as {@code ?hide_header=1}, so the rule buys a second parameter, not a barrier.</p>
 *
 * <p>Nothing here needs to be a barrier. These flags are cosmetic, the page is public to anyone
 * with the URL, and suppressing our own chrome discloses nothing. The things that ARE controls
 * live elsewhere: CSP {@code frame-ancestors} decides who may frame the page at all, and a named
 * {@code targetOrigin} decides who may receive what it posts.</p>
 *
 * <h2>What is deliberately NOT here</h2>
 *
 * <ul>
 *   <li><b>No {@code hide_footer}.</b> {@code PublicFooter} carries the Privacy and Cookies
 *       links, and it is the only place an invitee with no account is ever told what happens to
 *       the name and email address they are about to type. It renders embedded or not.</li>
 *   <li><b>No colour parameters.</b> Calendly lets the EMBEDDER recolour per placement
 *       ({@code background_color}, {@code text_color}, {@code primary_color}); we let the HOST
 *       set one look centrally, via {@code .inv-theme} and their banner. Accepting both would
 *       give colour two owners. Embedded pages instead drop their page background so the host's
 *       own page shows through, which is the part of the effect that actually matters.</li>
 * </ul>
 */
export interface EmbedOptions {
  /** {@code embed=1}: render as a component in somebody else's page, not as a destination. */
  embedded: boolean;
  /** {@code hide_header=1}: drop the banner/avatar/host-name card. Calendly's nearest is {@code hide_landing_page_details}. */
  hideHeader: boolean;
  /** {@code hide_details=1}: drop the "what you're booking" summary, keeping the slot picker. Calendly's {@code hide_event_type_details}. */
  hideDetails: boolean;
  /**
   * {@code embed_domain}: the host page's domain, as reported by the embedding site.
   *
   * <p><b>Untrusted.</b> It is set by whoever wrote the embed markup, so it is a label for
   * diagnostics, never an authorisation. Host only, no scheme, matching what Calendly sends.</p>
   */
  embedDomain: string | null;
  /**
   * {@code embed_origin}: scheme and host of the embedding page, e.g. {@code https://orq.ai}.
   *
   * <p>This is the {@code targetOrigin} we post the page height to. It is equally untrusted, and
   * it does not need to be trusted: naming an origin in {@code postMessage} is a restriction, not
   * a grant. If somebody frames us and claims an origin they do not control, the browser simply
   * refuses to deliver, so a lie fails closed. What it buys over {@code "*"} is that the message
   * cannot reach a frame other than the one that asked for it.</p>
   *
   * <p>Separate from {@code embed_domain} because a host alone forces us to guess the scheme, and
   * guessing https silently breaks auto-height for anyone testing over http. A silent failure is
   * the worst possible bug here: the embed looks fine and is simply the wrong height.</p>
   */
  embedOrigin: string | null;
}

/** A page opened directly. The shape every non-embedded render gets, shared so it is one object. */
export const NOT_EMBEDDED: EmbedOptions = {
  embedded: false,
  hideHeader: false,
  hideDetails: false,
  embedDomain: null,
  embedOrigin: null,
};

/**
 * Calendly writes its flags as {@code =1}, and hosts copying a snippet between the two products
 * will write {@code =1} too. {@code true} is accepted because it is the other thing people type.
 * Everything else, including {@code 0} and the empty string, is false.
 */
function flag(params: URLSearchParams, name: string): boolean {
  const raw = params.get(name);
  return raw === "1" || raw === "true";
}

export function parseEmbedOptions(search: string): EmbedOptions {
  const params = new URLSearchParams(search);
  if (!flag(params, "embed")) return NOT_EMBEDDED;
  return {
    embedded: true,
    hideHeader: flag(params, "hide_header"),
    hideDetails: flag(params, "hide_details"),
    embedDomain: params.get("embed_domain"),
    embedOrigin: safeOrigin(params.get("embed_origin")),
  };
}

/**
 * An {@code embed_origin} we are willing to hand to {@code postMessage}.
 *
 * <p>Only the scheme and host survive. Anything with a path, or a scheme that is not http(s), is
 * dropped rather than repaired: {@code postMessage} throws on a malformed {@code targetOrigin},
 * and a throw inside a ResizeObserver callback would break the page for a value that arrived from
 * the query string. Rejecting is also what makes {@code javascript:} and {@code data:} non-issues
 * here rather than something to reason about.</p>
 */
function safeOrigin(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    return url.origin;
  } catch {
    return null;
  }
}

/**
 * The parsed embed options for this page load. See the note above on why this reads once.
 *
 * <p>The {@code window} guard is not defensive noise. This runs in a RENDER BODY, not an effect,
 * and the catalog tests render these components with {@code renderToString} in vitest's node
 * environment, where there is no {@code window}. An unguarded read would throw there, and
 * throwing during render is precisely how the apex bundle has twice been served an empty page
 * with a healthy-looking 200 (see {@code catalog/CatalogApp.test.tsx}). No query string means
 * not embedded, which is the right answer anywhere there is no URL to read.</p>
 */
export function useEmbedOptions(): EmbedOptions {
  return React.useMemo(
    () => parseEmbedOptions(typeof window === "undefined" ? "" : window.location.search),
    [],
  );
}

/** The message an embedded page sends its host so the iframe can be sized to fit. */
export const HEIGHT_EVENT = "scheda.page_height";

/**
 * Report our rendered height to the page that framed us, whenever it changes.
 *
 * <p><b>This is not cosmetic.</b> An iframe has a fixed height chosen by whoever pasted the
 * snippet, and it has no idea how tall a month of slots is. Without this the host picks a number,
 * and every booking page is either clipped or floating in dead space. It is also why the shell
 * drops {@code min-h-screen} when embedded: a page that always claims the full viewport reports a
 * height that says nothing about its content.</p>
 *
 * <h2>Why the target origin is not "*"</h2>
 *
 * <p>A height is only a number, so {@code "*"} would leak nothing today. It is still wrong,
 * because this channel is the one a booking-completed event will use next, and a channel that
 * broadcasts to whoever happens to be framing us is the wrong shape to build that on. Naming the
 * origin costs nothing and fails closed: a page claiming an origin it does not control simply
 * receives nothing.</p>
 *
 * <p>Falls back to {@code "*"} only when there is no usable {@code embed_origin}, which means
 * somebody framed the page by hand rather than through our loader.</p>
 */
export function useReportHeight(
  embed: EmbedOptions,
  ref: React.RefObject<HTMLElement | null>,
): void {
  React.useEffect(() => {
    if (!embed.embedded) return;
    if (typeof window === "undefined" || typeof ResizeObserver === "undefined") return;
    // Not in a frame: nobody to tell. Also the case in the app's own tab.
    if (window.parent === window) return;

    const element = ref.current;
    if (!element) return;

    const target = embed.embedOrigin ?? "*";
    const send = () => {
      const height = Math.ceil(element.getBoundingClientRect().height);
      if (height <= 0) return;
      try {
        window.parent.postMessage({ event: HEIGHT_EVENT, payload: { height } }, target);
      } catch {
        // A rejected targetOrigin must not take the page down with it. The embed
        // degrades to whatever height the host hard-coded, which is Calendly's
        // normal behaviour anyway.
      }
    };

    send(); // Before any resize fires, or a page that never changes size never reports.
    const observer = new ResizeObserver(send);
    observer.observe(element);
    return () => observer.disconnect();
  }, [embed, ref]);
}
